<template>
	<div class="text-recommendation-wrapper">
		<div class="text-recommendation" :class="{ 'text-recommendation--single': isSingleTextRecommendation }">
			<div class="text-recommendation__content">
				<div class="text-recommendation__content__top">
					<span class="text-recommendation__content__top__headline">{{ displayedHeadline }}</span>
					<div class="text-recommendation__content__top__details">
						<span
							class="text-recommendation__content__top__details__readmore"
							@click="toggleCardModal(true)"
						>
							{{ $t('WEBAPP_READ_MORE') }}
						</span>
						<template v-if="showWatchedAt">
							<span class="text-recommendation__content__top__details__dash">-</span>
							<div class="text-recommendation__content__top__details__watchedAt">
								<div class="text-recommendation__content__top__details__watchedAt__content">
									<FontAwesomeIcon :icon="faCheck" />
									<span>{{ dateWatched }}</span>
								</div>
								<span class="tooltip"
									><FontAwesomeIcon :icon="faCheck" />
									{{ $t('WEBAPP_MARKED_AS_SEEN_ON', { date: dateWatched }) }}</span
								>
							</div>
						</template>
					</div>
				</div>

				<div class="text-recommendation__content__profile">
					<TextRecommendationCardTitleDetailProfile
						:profile="profile"
						:customProfileType="textRecommendation.customProfileType"
					/>

					<Popover v-if="showUserOpts" :key="`popover-${textRecommendation.id}`">
						<template #trigger>
							<FontAwesomeIcon :icon="faEllipsis" />
						</template>
						<template #content>
							<div class="popover">
								<div class="popover__item" @click="() => toggleTextRecommendationModal(true)">
									<FontAwesomeIcon :icon="faPencil" />
									<span>{{ $t('WEBAPP_EDIT') }}</span>
								</div>

								<div class="popover__item popover__item--danger" @click="deleteTextRecommendation">
									<FontAwesomeIcon :icon="faTrash" />
									<span>{{ $t('WEBAPP_DELETE') }}</span>
								</div>
							</div>
						</template>
					</Popover>
				</div>
			</div>

			<div v-if="textRecommendationHasVideo" class="text-recommendation__video" @click="handleVideoClick">
				<span class="text-recommendation__video__text">
					<FontAwesomeIcon :icon="faPlay" class="text-recommendation__video__text__icon" />
					{{ $t('WEBAPP_WATCH_THE_INTERVIEW') }}</span
				>
				<img
					class="text-recommendation__video__img"
					:src="`https://img.youtube.com/vi/${textRecommendation.video?.externalId}/default.jpg`"
					alt=""
				/>
			</div>

			<img
				class="background"
				:src="backgroundImageUrl"
				loading="lazy"
				fetchpriority="low"
				alt="background blur"
			/>
		</div>

		<TextRecommendationCardTitleDetailModal
			v-if="displayCardModal"
			:title="title"
			:textRecommendation="textRecommendation"
			:additionalContexts="additionalContexts"
			@close="() => toggleCardModal(false)"
		/>

		<TextRecommendationModalV2
			v-if="displayTextRecommendationModal"
			:title="title"
			:formData="textRecommendation"
			:editMode="true"
			@close="() => toggleTextRecommendationModal(false)"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import type { ProfileType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { ProfileInfoFragment } from '@/features/user-profile/graphql/fragments/ProfileInfo.fragment';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';

import TextRecommendationCardTitleDetailProfile from '@/features/why-to-watch/components/TextRecommendationCardTitleDetailProfile.vue';
import { TextRecommendationModalV2, TextRecommendationCardTitleDetailModal } from '@/lazy-load-components';
import TrailerModal from '@/components/TrailerModal.vue';
import Popover from '@/ui-components/Popover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useTextRecommendation } from '@/helpers/composables/useTextRecommendation';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { toastSuccess, toastError } from '@/helpers/vfm/toast-helper';

import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';

import { faCheck, faPlay, faEllipsis, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
const props = withDefaults(
	defineProps<{
		textRecommendation: TextRecommendationFragment;
		title?: TitleDetail;
		isSingleTextRecommendation?: boolean;
		hideMedia?: boolean;
		profile?: ProfileInfoFragment;
		hideBadge?: ProfileType[];
		hideAuthor?: boolean;
		additionalContexts?: SnowplowContext[];
	}>(),
	{
		hideMedia: false,
		isSingleTextRecommendation: false,
		hideBadge: () => [],
		additionalContexts: () => [],
	}
);

defineEmits<{ (e: 'posterClick'): void }>();

const { jwConfig } = useRuntimeConfig().public;

const textRecommendationTitle = computed(() => {
	return props.textRecommendation.title ?? props.title;
});

const { profile, textRecTitle, textRecommendationHasVideo, shareId } = useTextRecommendation({
	textRecommendation: () => props.textRecommendation,
	title: () => textRecommendationTitle.value ?? null,
});

const route = useRoute();
const fromRecShare = ref(false);
const showOriginalText = ref<boolean>(false);
const displayCardModal = ref<boolean>(false);
const displayTextRecommendationModal = ref(false);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);

const displayedHeadline = computed(() => {
	if (showOriginalText.value && originalHeadline.value) return originalHeadline.value;
	return headline.value;
});

const dateWatched = computed(() => {
	return Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	}).format(new Date(props.textRecommendation.watchedAt));
});

const showWatchedAt = computed(() => {
	return ['JW_EDITOR', 'USER', 'PRO_USER'].includes(props.textRecommendation.profile.profileType);
});

const showUserOpts = computed(() => props.textRecommendation.ownedByUser);

const backgroundImageUrl = computed(
	() =>
		`${jwConfig.IMAGESCALER_URL}${textRecTitle.value?.content.posterUrl
			?.replace('{profile}', 's166')
			.replace('{format}', 'avif')}`
);

async function handleVideoClick() {
	TrackingHelper.trackEvent(
		'youtube_started',
		{
			action: props.title?.objectType.toLowerCase(),
			label: 'titledetail',
			property: 'wtw_recommendation',
			value: 1,
		},
		props.additionalContexts
	);

	await ModalHelper.openModal(
		TrailerModal,
		{
			clips: [props.textRecommendation.video],
			title: props.title,
			source: 'titledetail',
			trackingProperty: 'wtw_recommendation',
			additionalContexts: props.additionalContexts,
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
}

function toggleCardModal(display: boolean) {
	displayCardModal.value = display;

	if (displayCardModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'read_more_clicked',
			},
			props.additionalContexts
		);
	}

	displayCardModal.value ? disableScroll() : enableScroll();
}

function toggleTextRecommendationModal(display: boolean) {
	displayTextRecommendationModal.value = display;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);
function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}
deleteRecommendation.onDone(() => {
	toastSuccess({
		message: 'Recommendation deleted',
		duration: 3000,
	});
});
deleteRecommendation.onError(() => {
	toastError({
		message: 'Failed to delete recommendation',
		duration: 3000,
	});
});

onMounted(() => {
	if (route.hash !== `#${shareId.value}`) return;

	// Waiting a tiny bit because anchor links work right away but the page content
	// is still rendering and shifting (on mounted renders like ads, experiments, etc.)
	// This helps improve the experience.
	setTimeout(() => {
		const elements = [...document.querySelectorAll(route.hash)];
		const element = elements.find(e => e != null && e.checkVisibility());
		if (element != null) {
			element?.scrollIntoView();
			fromRecShare.value = true;
		}
	}, 250);
});
</script>

<style scoped lang="scss">
.text-recommendation-wrapper {
	width: 100%;

	@media (max-width: 512px) {
		width: calc(100% - 8px) !important;
		margin-inline-end: 0 !important;
	}
}

.text-recommendation {
	display: flex;
	flex-direction: column;

	position: relative;
	overflow: hidden;
	background: linear-gradient(180deg, rgba(16, 23, 32, 0.96) 0%, rgba(16, 23, 32, 0.8) 100%);
	border-radius: 8px;
	height: 100%;
	min-height: 204px;

	&:last-child {
		margin-inline-end: 0;
	}

	&--single {
		width: 100%;
	}

	@media (max-width: 512px) {
		width: 100%;
	}

	&__content {
		padding: 24px;
		height: 100%;
		min-height: 204px;
		max-height: 215px;
		align-self: stretch;
		display: flex;
		flex-direction: column;
		gap: 16px;

		&__top {
			display: flex;
			flex-direction: column;
			gap: 8px;
			height: 100px;

			&__headline {
				color: #fff;
				font-size: 16px;
				font-weight: 400;
				line-height: 24px;
				letter-spacing: 0.24px;

				-webkit-box-orient: vertical;
				display: -webkit-box;
				line-clamp: 3;
				-webkit-line-clamp: 3;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			&__body {
				// Styles to hide element but keep it in DOM
				border: 0;
				clip: rect(0 0 0 0);
				height: 1px;
				margin: -1px;
				overflow: hidden;
				padding: 0;
				position: absolute;
				white-space: nowrap;
				width: 1px;
			}

			&__details {
				display: flex;
				align-items: center;
				gap: 8px;
				z-index: 2;
				width: fit-content;

				&__readmore {
					color: #78a6b8;
					font-size: 14px;
					font-weight: 400;
					line-height: 16px;
					letter-spacing: 0.21px;

					&:hover {
						cursor: pointer;
						text-decoration: underline;
					}
				}

				&__dash {
					color: #909398;
				}

				&__watchedAt {
					&__content {
						position: relative;
						color: #909398;
						font-size: 14px;
						font-weight: 400;
						line-height: 16px;
						letter-spacing: 0.21px;
						margin: 0;

						display: flex;
						align-items: center;
						gap: 4px;

						&:hover {
							cursor: pointer;
						}
					}

					.tooltip {
						display: none;
						position: absolute;
						left: 50%;
						transform: translateX(-50%);
						background: #232932;
						border-radius: 4px;
						white-space: nowrap;
						font-size: 14px;
						z-index: 10;
						pointer-events: none;
						opacity: 0;
						transition: opacity 0.15s;
						padding: 16px;
						color: #909398;
						font-weight: 400;
						line-height: 16px;
						letter-spacing: 0.21px;
						margin-top: 8px;
					}

					&:hover .tooltip {
						display: block;
						opacity: 1;
					}
				}
			}
		}

		&__profile {
			margin-top: auto;
			z-index: 1;

			display: flex;
			justify-content: space-between;
			align-items: flex-end;
		}
	}

	&__video {
		position: relative;
		display: flex;
		justify-content: space-between;
		align-items: center;
		border-radius: 8px;
		background: #1a2029;
		z-index: 2;
		width: 100%;
		box-shadow: -1px 1px 11px 1px rgba(0, 0, 0, 0.75);

		&:hover {
			cursor: pointer;
		}

		&__text {
			color: #fff;
			font-size: 14px;
			font-weight: 400;
			line-height: 24px;
			letter-spacing: 0.24px;
			padding: 8px 16px;

			&__icon {
				height: 16px;
				width: 16px;
			}
		}

		&__img {
			height: 40px;
			border-radius: 8px;
		}
	}

	.background {
		position: absolute;
		display: inline-block;
		background-size: cover;
		background-position: center;

		filter: blur(12px);
		z-index: 0;

		top: 0;
		left: 0;
		width: 100%;
		height: 100%;

		opacity: 0.1;
	}
}

.popover {
	display: flex;
	flex-direction: column;
	background-color: #0c131e;
	padding: 8px;
	border-radius: 8px;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 -4px 8px rgba(0, 0, 0, 0.1), 4px 0 8px rgba(0, 0, 0, 0.1),
		-4px 0 8px rgba(0, 0, 0, 0.1);

	&__item {
		display: flex;
		align-items: center;
		padding: 8px;
		gap: 8px;
		border-radius: 4px;

		&:hover {
			cursor: pointer;
			background-color: #222c38;
		}

		&--danger {
			color: #e30202;
		}
	}
}
</style>
